import { FC, useCallback } from 'react';

import { SearchIcon } from '@heroicons/react/outline';
import { GiftOrderSearchParams } from '@shared/api/giftOrder/giftOrder.hook';

import { OrderSearchResultsView } from '../components/OrderSearchResultsView';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';

export const GiftOrderSearchPage: FC = function GiftOrderSearchPage() {
  // 검색 입력 상태 — 새로고침(환불 후 reload 등) 후에도 유지되도록 sessionStorage에 저장
  const [phoneNumber, setPhoneNumber] = useSessionPersistedState(
    'giftOrderSearch.phoneNumber',
    '',
  );
  const [receiverName, setReceiverName] = useSessionPersistedState(
    'giftOrderSearch.receiverName',
    '',
  );

  // 검색 실행 상태 — 함께 저장해 새로고침 후 검색 결과까지 자동 복원
  const [searchParams, setSearchParams] =
    useSessionPersistedState<GiftOrderSearchParams | null>(
      'giftOrderSearch.searchParams',
      null,
    );
  const [hasSearched, setHasSearched] = useSessionPersistedState(
    'giftOrderSearch.hasSearched',
    false,
  );

  const handleSearch = useCallback(() => {
    if (!phoneNumber && !receiverName) {
      alert('검색 조건을 하나 이상 입력해주세요.');
      return;
    }

    const params: GiftOrderSearchParams = {};
    if (phoneNumber) params.phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (receiverName) params.receiverName = receiverName;

    setSearchParams(params);
    setHasSearched(true);
  }, [phoneNumber, receiverName, setSearchParams, setHasSearched]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  // 필터 초기화 — 입력값·검색 실행 상태를 모두 비워 sessionStorage 저장분까지 초기화
  const handleReset = useCallback(() => {
    setPhoneNumber('');
    setReceiverName('');
    setSearchParams(null);
    setHasSearched(false);
  }, [setPhoneNumber, setReceiverName, setSearchParams, setHasSearched]);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">주문 검색</h1>
        <p className="mt-1 text-sm text-gray-500">
          고객 응대용 - 핸드폰번호, 주문자 성함으로 검색
        </p>
      </div>

      {/* 검색 폼 */}
      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">핸드폰번호</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="010-1234-5678"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">주문자 성함</label>
            <input
              type="text"
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="홍길동"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <SearchIcon className="h-4 w-4" />
              검색
            </button>
            <button
              onClick={handleReset}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      <OrderSearchResultsView searchParams={searchParams} hasSearched={hasSearched} />
    </div>
  );
};
